/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-29 08:54:07
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import type {
  CreateRecommendationWidgetType,
  ManualRecommendationType,
  CreateManualRecommendationRequestType,
  EditManualRecommendationRequestType,
  RecommendationWidgetType,
  AIModelStatusRequestType
} from 'src/models/recommendation';
import {
  GET_SYNC_ORDER_STATUS_FAILURE,
  GET_SYNC_ORDER_STATUS_REQUEST,
  GET_SYNC_ORDER_STATUS_SUCCESS,
  ManualRecommendationParams
} from 'stores/flow-type/recommendation.type';

import apiService from 'src/services/api-service';
import {
  GET_RECOMMENDATION_WIDGETS_REQUEST,
  GET_RECOMMENDATION_WIDGETS_SUCCESS,
  GET_RECOMMENDATION_WIDGETS_FAILURE,
  GET_RECOMMENDATION_WIDGET_REQUEST,
  GET_RECOMMENDATION_WIDGET_SUCCESS,
  GET_RECOMMENDATION_WIDGET_FAILURE,
  DELETE_RECOMMENDATION_WIDGET_REQUEST,
  DELETE_RECOMMENDATION_WIDGET_SUCCESS,
  DELETE_RECOMMENDATION_WIDGET_FAILURE,
  CREATE_RECOMMENDATION_WIDGET_REQUEST,
  CREATE_RECOMMENDATION_WIDGET_SUCCESS,
  CREATE_RECOMMENDATION_WIDGET_FAILURE,
  UPDATE_RECOMMENDATION_WIDGET_REQUEST,
  UPDATE_RECOMMENDATION_WIDGET_SUCCESS,
  UPDATE_RECOMMENDATION_WIDGET_FAILURE,
  UPDATE_RECOMMENDATION_WIDGET_ACTION_REQUEST,
  UPDATE_RECOMMENDATION_WIDGET_ACTION_SUCCESS,
  UPDATE_RECOMMENDATION_WIDGET_ACTION_FAILURE,
  DUPLICATE_RECOMMENDATION_WIDGET_REQUEST,
  DUPLICATE_RECOMMENDATION_WIDGET_SUCCESS,
  DUPLICATE_RECOMMENDATION_WIDGET_FAILURE,
  GET_MANUAL_RECOMMENDATION_REQUEST,
  GET_MANUAL_RECOMMENDATION_SUCCESS,
  GET_MANUAL_RECOMMENDATION_FAILURE,
  DELETE_MANUAL_RECOMMENDATION_REQUEST,
  DELETE_MANUAL_RECOMMENDATION_SUCCESS,
  DELETE_MANUAL_RECOMMENDATION_FAILURE,
  SEARCH_MANUAL_RECOMMENDATION_REQUEST,
  SEARCH_MANUAL_RECOMMENDATION_SUCCESS,
  SEARCH_MANUAL_RECOMMENDATION_FAILURE,
  CLEAR_SEARCH_MANUAL_RECOMMENDATION,
  REMOVE_RECOMMENDATION_FROM_SEARCH_RESULT,
  CREATE_MANUAL_RECOMMENDATION_REQUEST,
  CREATE_MANUAL_RECOMMENDATION_SUCCESS,
  CREATE_MANUAL_RECOMMENDATION_FAILURE,
  GET_MANUAL_RECOMMENDATION_BY_ID_REQUEST,
  GET_MANUAL_RECOMMENDATION_BY_ID_SUCCESS,
  GET_MANUAL_RECOMMENDATION_BY_ID_FAILURE,
  EDIT_MANUAL_RECOMMENDATION_REQUEST,
  EDIT_MANUAL_RECOMMENDATION_SUCCESS,
  EDIT_MANUAL_RECOMMENDATION_FAILURE,
  UPDATE_SEARCH_MANUAL_RECOMMENDATION_STATUS,
  ACTIVATE_AI_BASE_FAILURE,
  ACTIVATE_AI_BASE_REQUEST,
  ACTIVATE_AI_BASE_SUCCESS,
  GET_AI_MODEL_STATUSES_FAILURE,
  GET_AI_MODEL_STATUSES_REQUEST,
  GET_AI_MODEL_STATUSES_SUCCESS,
  GET_AI_MODEL_STATUS_FAILURE,
  GET_AI_MODEL_STATUS_REQUEST,
  GET_AI_MODEL_STATUS_SUCCESS,
  GET_AI_SYNC_STATUS_FAILURE,
  GET_AI_SYNC_STATUS_REQUEST,
  GET_AI_SYNC_STATUS_SUCCESS,
  CHECK_ENABLE_BOOST_CORE_REQUEST,
  CHECK_ENABLE_BOOST_CORE_SUCCESS,
  CHECK_ENABLE_BOOST_CORE_FAILURE,
  MARK_DONE_TOUR_WIDGET_REQUEST,
  MARK_DONE_TOUR_WIDGET_SUCCESS,
  MARK_DONE_TOUR_WIDGET_FAILURE,
  GET_DEFAULT_DESIGN_SETTINGS_REQUEST,
  GET_DEFAULT_DESIGN_SETTINGS_SUCCESS,
  GET_DEFAULT_DESIGN_SETTINGS_FAILURE,
  SYNC_WIDGETS_REQUEST,
  SYNC_WIDGETS_SUCCESS,
  SYNC_WIDGETS_FAILURE,
  GET_RECOMMENDATION_GENERAL_SETTINGS_REQUEST,
  GET_RECOMMENDATION_GENERAL_SETTINGS_SUCCESS,
  GET_RECOMMENDATION_GENERAL_SETTINGS_FAILURE,
  UPDATE_RECOMMENDATION_GENERAL_SETTINGS_REQUEST,
  UPDATE_RECOMMENDATION_GENERAL_SETTINGS_SUCCESS,
  UPDATE_RECOMMENDATION_GENERAL_SETTINGS_FAILURE
} from 'stores/flow-type/recommendation.type';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to get recommendation widgets
 * @return {object} The RSAA object for API middleware
 */
export const getRecommendationWidgets = (i18n: I18n, isOnBoarding: boolean = false) => {
  const queries = isOnBoarding
    ? {
        isOnBoarding
      }
    : {};
  return apiService.sendRequest(
    'admin-v3.recommendation.get-widgets',
    {
      queries
    },
    [
      GET_RECOMMENDATION_WIDGETS_REQUEST,
      {
        type: GET_RECOMMENDATION_WIDGETS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, isOnBoarding };
        }
      },
      {
        type: GET_RECOMMENDATION_WIDGETS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.getRecommendationWidgets_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get recommendation widget BY Id
 * @return {object} The RSAA object for API middleware
 */
export const getRecommendationWidget = (widgetId: string, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.get-widget', { params: { widgetId } }, [
    GET_RECOMMENDATION_WIDGET_REQUEST,
    {
      type: GET_RECOMMENDATION_WIDGET_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_RECOMMENDATION_WIDGET_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.getRecommendationWidget_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to add new recommendation widget
 * @return {object} The RSAA object for API middleware
 */
export const createRecommendationWidget = (
  recommendationWidget: CreateRecommendationWidgetType,
  i18n: I18n
) => {
  return apiService.sendRequest(
    'admin-v3.recommendation.create-widget',
    { body: recommendationWidget },
    [
      CREATE_RECOMMENDATION_WIDGET_REQUEST,
      {
        type: CREATE_RECOMMENDATION_WIDGET_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          mixpanelV3Helper.trackEventData('Add New Recommendation Widget', {
            feature: 'Recommendation',
            pageType: recommendationWidget.pageType,
            recommendationType: recommendationWidget.recommendationType
          });

          // Track Full Story event
          trackFSEvent('Add New Recommendation Widget', {
            feature: 'Recommendation',
            pageType: recommendationWidget.pageType,
            recommendationType: recommendationWidget.recommendationType
          });

          return { data };
        }
      },
      {
        type: CREATE_RECOMMENDATION_WIDGET_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.createRecommendationWidget_Failure'),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to recommendation widget
 * @return {object} The RSAA object for API middleware
 */
export const deleteRecommendationWidget = (widgetId: string, i18n: I18n) => {
  const params = { widgetId };
  return apiService.sendRequest('admin-v3.recommendation.delete-widget', { params }, [
    DELETE_RECOMMENDATION_WIDGET_REQUEST,
    {
      type: DELETE_RECOMMENDATION_WIDGET_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'info',
          i18n.translate('AdminHTML.Recommendation.Actions.deleteRecommendationWidget_Success'),
          i18n
        );

        // Track Full Story event
        const recommendationWidgets = _.get(state, 'recommendation.recommendationWidgets', []);
        const deleteWidget = recommendationWidgets.find(e => e.id === widgetId);

        trackFSEvent('Delete Recommendation Widget', {
          feature: 'Recommendation',
          pageType: deleteWidget?.pageType,
          recommendationType: deleteWidget?.recommendationType
        });

        return { data };
      }
    },
    {
      type: DELETE_RECOMMENDATION_WIDGET_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.deleteRecommendationWidget_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to update widget
 * @params widgetStatus
 * @params i18n
 * @return {object} The RSAA object for API middleware
 */
export const updateRecommendationWidget = (
  widgetId: string,
  widgetData: RecommendationWidgetType,
  meta: Object,
  i18n: I18n
) => {
  const params = { widgetId };
  return apiService.sendRequest(
    'admin-v3.recommendation.update-widget',
    { params, body: widgetData },
    [
      UPDATE_RECOMMENDATION_WIDGET_REQUEST,
      {
        type: UPDATE_RECOMMENDATION_WIDGET_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          // Track Full Story event
          trackFSEvent('Edit Recommendation Widget', {
            feature: 'Recommendation',
            pageType: widgetData?.pageType,
            recommendationType: widgetData?.recommendationType
          });

          return { data };
        },
        meta
      },
      {
        type: UPDATE_RECOMMENDATION_WIDGET_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.updateRecommendationWidget_Failure'),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to update widget action
 * @params widgetId
 * @params actionType
 * @params meta
 * @params i18n
 * @return {object} The RSAA object for API middleware
 */
export const updateRecommendationWidgetAction = (
  widgetId: string,
  actionType: string,
  meta: Object,
  i18n: I18n
) => {
  const params = { widgetId };
  return apiService.sendRequest(
    'admin-v3.recommendation.update-widget-action',
    { params, body: { actionType } },
    [
      UPDATE_RECOMMENDATION_WIDGET_ACTION_REQUEST,
      {
        type: UPDATE_RECOMMENDATION_WIDGET_ACTION_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          if (actionType === 'pause') {
            const message = i18n.translate(
              'AdminHTML.Recommendation.Actions.updateRecommendationWidgetAction_Pause_Success'
            );
            await showSABToast(message, false);
          }

          // Track Full Story event
          const recommendationWidgets = _.get(state, 'recommendation.recommendationWidgets', []);
          const widget = recommendationWidgets.find(e => e.id === widgetId);

          trackFSEvent(
            `${actionType[0]?.toUpperCase()}${actionType?.slice(1)} Recommendation Widget`,
            {
              feature: 'Recommendation',
              pageType: widget?.pageType,
              recommendationType: widget?.recommendationType
            }
          );

          return { data };
        },
        meta
      },
      {
        type: UPDATE_RECOMMENDATION_WIDGET_ACTION_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate(
              'AdminHTML.Recommendation.Actions.updateRecommendationWidgetAction_Failure'
            ),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to duplicate widget
 * @params widgetId
 * @params meta
 * @params i18n
 * @return {object} The RSAA object for API middleware
 */
export const duplicateRecommendationWidget = (widgetId: string, meta: Object, i18n: I18n) => {
  const body = { widgetId };
  return apiService.sendRequest('admin-v3.recommendation.duplicate-widget', { body }, [
    DUPLICATE_RECOMMENDATION_WIDGET_REQUEST,
    {
      type: DUPLICATE_RECOMMENDATION_WIDGET_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        const { id } = data;
        const message = i18n.translate(
          'AdminHTML.Recommendation.Actions.duplicateRecommendationWidget_Success',
          {
            nt1: id,
            nt2: widgetId
          }
        );
        await showSABToast(message, false);

        // Track Full Story event
        const recommendationWidgets = _.get(state, 'recommendation.recommendationWidgets', []);
        const widget = recommendationWidgets.find(e => e.id === widgetId);

        trackFSEvent('Duplicate Recommendation Widget', {
          feature: 'Recommendation',
          pageType: widget?.pageType,
          recommendationType: widget?.recommendationType
        });

        return { data };
      },
      meta
    },
    {
      type: DUPLICATE_RECOMMENDATION_WIDGET_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.duplicateRecommendationWidget_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * call to get manual recommendations
 * @return {object} The RSAA object for API middleware
 */
export const getManualRecommendations = (
  queries: ManualRecommendationParams = {},
  type: ManualRecommendationType,
  i18n: I18n
) => {
  return apiService.sendRequest(
    'admin-v3.recommendation.get-manual-recommendations',
    {
      queries: queries
    },
    [
      {
        type: GET_MANUAL_RECOMMENDATION_REQUEST,
        payload: {
          type
        }
      },
      {
        type: GET_MANUAL_RECOMMENDATION_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, type };
        }
      },
      {
        type: GET_MANUAL_RECOMMENDATION_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.getManualRecommendations_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to search manual recommendations
 * @return {object} The RSAA object for API middleware
 */
export const searchManualRecommendations = (
  queries: ManualRecommendationParams = {},
  i18n: I18n
) => {
  return apiService.sendRequest(
    'admin-v3.recommendation.get-manual-recommendations',
    {
      queries: queries
    },
    [
      SEARCH_MANUAL_RECOMMENDATION_REQUEST,
      {
        type: SEARCH_MANUAL_RECOMMENDATION_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SEARCH_MANUAL_RECOMMENDATION_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.getManualRecommendations_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * clear manual recommendation search result
 * @return
 */
export const clearSearchManualRecommendation = () => {
  return { type: CLEAR_SEARCH_MANUAL_RECOMMENDATION, payload: null };
};

/**
 * update search manual recommendation status
 * @return
 */
export const updateSearchManualRecommendationStatus = (value: boolean) => {
  return { type: UPDATE_SEARCH_MANUAL_RECOMMENDATION_STATUS, payload: value };
};

/**
 * call to delete manual recommendation
 * @return {object} The RSAA object for API middleware
 */
export const deleteManualRecommendation = (recommendationId: string, i18n: I18n) => {
  const params = { recommendationId };
  return apiService.sendRequest(
    'admin-v3.recommendation.delete-manual-recommendation',
    { params },
    [
      DELETE_MANUAL_RECOMMENDATION_REQUEST,
      {
        type: DELETE_MANUAL_RECOMMENDATION_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'info',
            i18n.translate('AdminHTML.Recommendation.Actions.deleteManualRecommendation_Success'),
            i18n
          );

          mixpanelV3Helper.trackEventData('Delete Hand-picked Rule', {
            feature: 'Recommendation'
          });

          return { data };
        }
      },
      {
        type: DELETE_MANUAL_RECOMMENDATION_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.deleteManualRecommendation_Failure'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * remove manual recommendation from search result
 * @return
 */
export const removeRecommendationFromSearchResult = recommendationId => {
  return { type: REMOVE_RECOMMENDATION_FROM_SEARCH_RESULT, payload: recommendationId };
};

/**
 * call to add new manual recommendation
 * @return {object} The RSAA object for API middleware
 */
export const createManualRecommendation = (
  manualRecommendation: CreateManualRecommendationRequestType,
  i18n: I18n
) => {
  return apiService.sendRequest(
    'admin-v3.recommendation.create-manual-recommendation',
    { body: manualRecommendation },
    [
      CREATE_MANUAL_RECOMMENDATION_REQUEST,
      {
        type: CREATE_MANUAL_RECOMMENDATION_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          mixpanelV3Helper.trackEventData('Add Hand-picked Rule', {
            feature: 'Recommendation'
          });

          return { data };
        }
      },
      {
        type: CREATE_MANUAL_RECOMMENDATION_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.createManualRecommendation_Failure'),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to get manual recommendation by id
 * @return {object} The RSAA object for API middleware
 */
export const getManualRecommendationById = (recommendationId: string, i18n: I18n) => {
  const params = { recommendationId };
  return apiService.sendRequest(
    'admin-v3.recommendation.get-manual-recommendation-by-id',
    { params },
    [
      GET_MANUAL_RECOMMENDATION_BY_ID_REQUEST,
      {
        type: GET_MANUAL_RECOMMENDATION_BY_ID_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          return { data };
        }
      },
      {
        type: GET_MANUAL_RECOMMENDATION_BY_ID_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.getManualRecommendation_Failure'),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to edit manual recommendation
 * @return {object} The RSAA object for API middleware
 */
export const editManualRecommendation = (
  recommendationId: string,
  manualRecommendation: EditManualRecommendationRequestType,
  i18n: I18n
) => {
  const params = { recommendationId };
  return apiService.sendRequest(
    'admin-v3.recommendation.edit-manual-recommendation',
    { params, body: manualRecommendation },
    [
      EDIT_MANUAL_RECOMMENDATION_REQUEST,
      {
        type: EDIT_MANUAL_RECOMMENDATION_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          mixpanelV3Helper.trackEventData('Edit Hand-picked Rule', {
            feature: 'Recommendation'
          });

          return { data };
        }
      },
      {
        type: EDIT_MANUAL_RECOMMENDATION_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.editManualRecommendation_Failure'),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};

/**
 * activate AI base
 * @return {object} The RSAA object for API middleware
 */
export const activateAIBase = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.active-AI-base', {}, [
    ACTIVATE_AI_BASE_REQUEST,
    {
      type: ACTIVATE_AI_BASE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: ACTIVATE_AI_BASE_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.getManualRecommendation_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * get AI sync status
 * @return {object} The RSAA object for API middleware
 */
export const getAISyncStatus = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.get-AI-sync-status', {}, [
    GET_AI_SYNC_STATUS_REQUEST,
    {
      type: GET_AI_SYNC_STATUS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_AI_SYNC_STATUS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.getManualRecommendation_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * get AI model statuses
 * @return {object} The RSAA object for API middleware
 */
export const getAIModelStatuses = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.get-AI-base-statuses', {}, [
    GET_AI_MODEL_STATUSES_REQUEST,
    {
      type: GET_AI_MODEL_STATUSES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_AI_MODEL_STATUSES_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.getManualRecommendation_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * get AI model status
 * @return {object} The RSAA object for API middleware
 */
export const getAIModelStatus = (queries: AIModelStatusRequestType = {}, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.get-AI-base-status', { queries }, [
    GET_AI_MODEL_STATUS_REQUEST,
    {
      type: GET_AI_MODEL_STATUS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data, type: queries.modelType };
      }
    },
    {
      type: GET_AI_MODEL_STATUS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.getManualRecommendation_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * get AI model status
 * @return {object} The RSAA object for API middleware
 */
export const getSyncOrderStatus = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.get-sync-order-status', {}, [
    GET_SYNC_ORDER_STATUS_REQUEST,
    {
      type: GET_SYNC_ORDER_STATUS_SUCCESS,
      payload: async (action, state, res) => {
        return await getJSON(res);
      }
    },
    {
      type: GET_SYNC_ORDER_STATUS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.getManualRecommendation_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * check is live theme  enabled boost core
 * @return {object} The RSAA object for API middleware
 */
export const checkEnableBoostCore = (queries = {}, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.check-enable-boost-core', { queries }, [
    CHECK_ENABLE_BOOST_CORE_REQUEST,
    {
      type: CHECK_ENABLE_BOOST_CORE_SUCCESS,
      payload: async (action, state, res) => {
        return await getJSON(res);
      }
    },
    {
      type: CHECK_ENABLE_BOOST_CORE_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.checkEnableBoostCore_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * update flag to mark tour done
 * @return {object} The RSAA object for API middleware
 */
export const markDoneTourWidget = (meta, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.mark-done-tour-widget', {}, [
    MARK_DONE_TOUR_WIDGET_REQUEST,
    {
      type: MARK_DONE_TOUR_WIDGET_SUCCESS,
      payload: async (action, state, res) => {
        return await getJSON(res);
      },
      meta
    },
    {
      type: MARK_DONE_TOUR_WIDGET_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.markDoneTour_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * get default design settings
 * @return {object} The RSAA object for API middleware
 */
export const getDefaultDesignSettings = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.get-default-design-settings', {}, [
    GET_DEFAULT_DESIGN_SETTINGS_REQUEST,
    {
      type: GET_DEFAULT_DESIGN_SETTINGS_SUCCESS,
      payload: async (action, state, res) => {
        return await getJSON(res);
      }
    },
    {
      type: GET_DEFAULT_DESIGN_SETTINGS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.getDefaultDesignSettings_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * Sync live theme to update widget status
 * @return {object} The RSAA object for API middleware
 */
export const syncWidgets = (meta: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.sync-widgets', {}, [
    {
      type: SYNC_WIDGETS_REQUEST,
      payload: async (action, state, res) => {
        await processRes(
          res,
          'info',
          i18n.translate('AdminHTML.Recommendation.Actions.syncWidgets_Request'),
          i18n
        );
        return {};
      }
    },
    {
      type: SYNC_WIDGETS_SUCCESS,
      payload: async (action, state, res) => {
        return await getJSON(res);
      },
      meta
    },
    {
      type: SYNC_WIDGETS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.syncWidgets_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * get recommendation general settings
 * @return {object} The RSAA object for API middleware
 */
export const getGeneralSettings = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.get-general-settings', {}, [
    GET_RECOMMENDATION_GENERAL_SETTINGS_REQUEST,
    {
      type: GET_RECOMMENDATION_GENERAL_SETTINGS_SUCCESS,
      payload: async (action, state, res) => {
        return await getJSON(res);
      }
    },
    {
      type: GET_RECOMMENDATION_GENERAL_SETTINGS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate(
            'AdminHTML.Recommendation.Actions.getRecommendationGeneralSetting_Failure'
          ),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * update recommendation general settings
 * @return {object} The RSAA object for API middleware
 */
export const updateGeneralSettings = (generalSettings, i18n: I18n) => {
  const requestBody = {
    ...generalSettings,
    includeProducts: generalSettings.includeProducts.map((p) => p.id),
    excludeProducts: generalSettings.excludeProducts.map((p) => p.id),
  }
  return apiService.sendRequest(
    'admin-v3.recommendation.update-general-settings',
    { body: requestBody },
    [
      UPDATE_RECOMMENDATION_GENERAL_SETTINGS_REQUEST,
      {
        type: UPDATE_RECOMMENDATION_GENERAL_SETTINGS_SUCCESS,
        payload: async (action, state, res) => {
          return await generalSettings;
        }
      },
      {
        type: UPDATE_RECOMMENDATION_GENERAL_SETTINGS_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate(
              'AdminHTML.Recommendation.Actions.getRecommendationGeneralSetting_Failure'
            ),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};
